import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { Modal, IconArrowHead } from '../../../components';

import css from './ListingHeroImageZoom.module.css';

const MODAL_ID = 'ListingPage.imageZoom';
const ZOOM_STEPS = [1, 1.5, 2, 2.5, 3];
const DRAG_THRESHOLD_PX = 6;

/**
 * Hero listing image that opens a zoom popup with pan and level controls.
 *
 * @component
 * @param {Object} props
 * @param {string[]} props.images - Gallery image URLs
 * @param {number} props.activeIndex - Currently selected image index
 * @param {Function} props.onActiveIndexChange - Sync gallery index when navigating in modal
 * @param {string} props.alt - Image alt text
 * @param {Function} props.onManageDisableScrolling - Sharetribe scroll lock handler
 * @param {string} [props.className] - Hero wrapper class
 * @param {React.ReactNode} [props.overlay] - e.g. wishlist button
 */
const ListingHeroImageZoom = props => {
  const {
    images,
    activeIndex,
    onActiveIndexChange,
    alt,
    onManageDisableScrolling,
    className,
    overlay,
  } = props;
  const intl = useIntl();
  const dragRef = useRef({ active: false, moved: false, startX: 0, startY: 0, panX: 0, panY: 0 });

  const [modalOpen, setModalOpen] = useState(false);
  const [zoomIndex, setZoomIndex] = useState(0);
  const [pan, setPan] = useState({ x: 0, y: 0 });

  const safeIndex = Math.min(Math.max(activeIndex, 0), Math.max(images.length - 1, 0));
  const currentSrc = images[safeIndex];
  const zoomScale = ZOOM_STEPS[zoomIndex];
  const canZoomIn = zoomIndex < ZOOM_STEPS.length - 1;
  const canZoomOut = zoomIndex > 0;
  const isZoomed = zoomScale > 1;

  const resetModalView = useCallback(() => {
    setZoomIndex(0);
    setPan({ x: 0, y: 0 });
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    resetModalView();
  }, [resetModalView]);

  const openModal = () => {
    resetModalView();
    setModalOpen(true);
  };

  useEffect(() => {
    if (modalOpen) {
      resetModalView();
    }
  }, [safeIndex, modalOpen, resetModalView]);

  const setZoomByIndex = nextIndex => {
    const clamped = Math.min(Math.max(nextIndex, 0), ZOOM_STEPS.length - 1);
    setZoomIndex(clamped);
    if (ZOOM_STEPS[clamped] === 1) {
      setPan({ x: 0, y: 0 });
    }
  };

  const zoomIn = () => {
    if (canZoomIn) {
      setZoomByIndex(zoomIndex + 1);
    }
  };

  const zoomOut = () => {
    if (canZoomOut) {
      setZoomByIndex(zoomIndex - 1);
    }
  };

  const goToPrev = () => {
    if (images.length <= 1) {
      return;
    }
    const next = safeIndex === 0 ? images.length - 1 : safeIndex - 1;
    onActiveIndexChange(next);
  };

  const goToNext = () => {
    if (images.length <= 1) {
      return;
    }
    const next = safeIndex === images.length - 1 ? 0 : safeIndex + 1;
    onActiveIndexChange(next);
  };

  const handleHeroClick = e => {
    if (e.target.closest('button, a')) {
      return;
    }
    openModal();
  };

  const handleImageClick = () => {
    if (dragRef.current.moved) {
      dragRef.current.moved = false;
      return;
    }
    if (isZoomed) {
      zoomOut();
    } else {
      zoomIn();
    }
  };

  const handlePointerDown = e => {
    if (!isZoomed || e.button !== 0) {
      return;
    }
    dragRef.current = {
      active: true,
      moved: false,
      startX: e.clientX,
      startY: e.clientY,
      panX: pan.x,
      panY: pan.y,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = e => {
    if (!dragRef.current.active) {
      return;
    }
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    if (Math.abs(dx) > DRAG_THRESHOLD_PX || Math.abs(dy) > DRAG_THRESHOLD_PX) {
      dragRef.current.moved = true;
    }
    setPan({
      x: dragRef.current.panX + dx,
      y: dragRef.current.panY + dy,
    });
  };

  const handlePointerEnd = e => {
    if (dragRef.current.active) {
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch (err) {
        // Pointer may already be released
      }
    }
    dragRef.current.active = false;
  };

  const handleWheel = e => {
    if (!modalOpen) {
      return;
    }
    e.preventDefault();
    if (e.deltaY < 0) {
      zoomIn();
    } else {
      zoomOut();
    }
  };

  const openLabel = intl.formatMessage({ id: 'ListingPage.openImageZoom' });

  const modalImageStyle = {
    '--modal-zoom-scale': zoomScale,
    '--modal-pan-x': `${pan.x}px`,
    '--modal-pan-y': `${pan.y}px`,
  };

  return (
    <>
      <div
        className={classNames(css.root, className)}
        onClick={handleHeroClick}
        role="button"
        tabIndex={0}
        aria-label={openLabel}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openModal();
          }
        }}
      >
        <img src={currentSrc} alt={alt} className={css.heroImage} draggable={false} />
        <span className={css.zoomHint} aria-hidden="true">
          <span className={css.zoomHintIcon}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.75" />
              <path
                d="M20 20l-3.5-3.5"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
              />
              <path
                d="M11 8v6M8 11h6"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
              />
            </svg>
          </span>
          <FormattedMessage id="ListingPage.clickToZoom" />
        </span>
        {overlay ? <div className={css.overlaySlot}>{overlay}</div> : null}
      </div>

      <Modal
        id={MODAL_ID}
        isOpen={modalOpen}
        onClose={closeModal}
        usePortal
        lightCloseButton
        closeOnBackdropClick
        onManageDisableScrolling={onManageDisableScrolling}
        scrollLayerClassName={css.modalScrollLayer}
        containerClassName={css.modalContainer}
        contentClassName={css.modalContent}
      >
        <div className={css.modalBody}>
          <div className={css.modalToolbar}>
            <div className={css.zoomControls}>
              <button
                type="button"
                className={css.toolbarButton}
                onClick={zoomOut}
                disabled={!canZoomOut}
                aria-label={intl.formatMessage({ id: 'ListingPage.zoomOutButton' })}
              >
                <span aria-hidden="true">−</span>
              </button>
              <span className={css.zoomLevel}>{Math.round(zoomScale * 100)}%</span>
              <button
                type="button"
                className={css.toolbarButton}
                onClick={zoomIn}
                disabled={!canZoomIn}
                aria-label={intl.formatMessage({ id: 'ListingPage.zoomInButton' })}
              >
                <span aria-hidden="true">+</span>
              </button>
            </div>
            {images.length > 1 ? (
              <span className={css.imageCounter}>
                <FormattedMessage
                  id="ListingPage.imageZoomCounter"
                  values={{ index: safeIndex + 1, count: images.length }}
                />
              </span>
            ) : null}
          </div>

          <div className={css.modalStage}>
            {images.length > 1 ? (
              <button
                type="button"
                className={classNames(css.navButton, css.navButtonPrev)}
                onClick={goToPrev}
                aria-label={intl.formatMessage({ id: 'ListingPage.imageZoomPrev' })}
              >
                <IconArrowHead direction="left" size="big" />
              </button>
            ) : null}

            <div
              className={classNames(css.imageViewport, {
                [css.imageViewportZoomed]: isZoomed,
              })}
              onWheel={handleWheel}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerEnd}
              onPointerCancel={handlePointerEnd}
            >
              <button
                type="button"
                className={css.modalImageButton}
                onClick={handleImageClick}
                aria-label={
                  isZoomed
                    ? intl.formatMessage({ id: 'ListingPage.zoomOutImage' })
                    : intl.formatMessage({ id: 'ListingPage.zoomInImage' })
                }
              >
                <img
                  src={currentSrc}
                  alt={alt}
                  className={css.modalImage}
                  style={modalImageStyle}
                  draggable={false}
                />
              </button>
            </div>

            {images.length > 1 ? (
              <button
                type="button"
                className={classNames(css.navButton, css.navButtonNext)}
                onClick={goToNext}
                aria-label={intl.formatMessage({ id: 'ListingPage.imageZoomNext' })}
              >
                <IconArrowHead direction="right" size="big" />
              </button>
            ) : null}
          </div>

          {images.length > 1 ? (
            <div className={css.modalThumbs}>
              {images.map((img, i) => (
                <button
                  key={`zoom-thumb-${i}`}
                  type="button"
                  className={classNames(css.modalThumb, {
                    [css.modalThumbActive]: safeIndex === i,
                  })}
                  onClick={() => onActiveIndexChange(i)}
                  aria-label={intl.formatMessage(
                    { id: 'ListingPage.imageZoomThumb' },
                    { index: i + 1 }
                  )}
                  aria-current={safeIndex === i}
                >
                  <img src={img} alt="" className={css.modalThumbImage} draggable={false} />
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </Modal>
    </>
  );
};

export default ListingHeroImageZoom;
